/**
 * Output Fields Section Component
 *
 * Displays output fields with their types.
 * Can be placed independently in the node config panel.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useNodeFields } from '../hooks/useNodeFields';
import type { OutputFieldDefinition, FieldType } from '@/types/workflowFields';
import { getFieldTypeLabel, getFieldTypeColor } from '@/types/workflowFields';

/** Custom input field for source selection */
interface CustomInputField {
  key: string;
  type: FieldType;
  label?: string;
}

interface OutputFieldsSectionProps {
  nodeId: string;
  isDisabled?: boolean;
  /** Optional title override */
  title?: string;
  /** Show/hide add field button (only for nodes that allow custom outputs) */
  showAddButton?: boolean;
  /** Custom border color class */
  borderColorClass?: string;
  /** Custom background color class */
  bgColorClass?: string;
  /** Available custom input fields for value source selection */
  customInputFields?: CustomInputField[];
  /** Schema-derived output fields (from output_schema selection) - shown as non-removable */
  schemaOutputFields?: OutputFieldDefinition[];
}

// Field type options for adding custom fields
const FIELD_TYPE_OPTIONS: { value: FieldType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
  { value: 'image_url', label: 'Image URL' },
];

export const OutputFieldsSection: React.FC<OutputFieldsSectionProps> = ({
  nodeId,
  isDisabled = false,
  title = '输出字段',
  showAddButton = true,
  borderColorClass = 'border-green-200',
  bgColorClass = 'bg-green-50/50',
  customInputFields = [],
  schemaOutputFields = [],
}) => {
  const [showTypePopover, setShowTypePopover] = useState(false);
  const [editingNewFieldKey, setEditingNewFieldKey] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowTypePopover(false);
      }
    };
    if (showTypePopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTypePopover]);

  const {
    nodeTypeSpec,
    outputFields,
    addOutputField,
    updateOutputField,
    updateOutputFieldSource,
    removeOutputField,
  } = useNodeFields(nodeId);

  if (!nodeTypeSpec) {
    return null;
  }

  const handleAddFieldWithType = (type: FieldType) => {
    // Generate a temporary unique key
    const tempKey = `output_${Date.now()}`;
    const field: OutputFieldDefinition = {
      key: tempKey,
      type,
      label: '',
      required: false,
      indexed: false,
    };
    addOutputField(field);
    setShowTypePopover(false);
    // Set this field as being edited
    setEditingNewFieldKey(tempKey);
  };

  const handleNewFieldNameSet = (oldKey: string, newKey: string) => {
    if (newKey.trim()) {
      updateOutputField(oldKey, newKey.trim());
    } else {
      // If empty, remove the field
      removeOutputField(oldKey);
    }
    setEditingNewFieldKey(null);
  };

  const canAddCustomOutputs = nodeTypeSpec.allows_custom_outputs !== false && showAddButton;

  // Combine output fields with proper override priority:
  // 1. If schema fields exist, they REPLACE default fields entirely
  // 2. Custom fields are always added (if not already in schema)
  const allOutputFields = React.useMemo(() => {
    const hasSchemaFields = schemaOutputFields.length > 0;
    const schemaKeys = new Set(schemaOutputFields.map(f => f.key));

    // Custom fields that aren't overridden by schema
    const customFields = outputFields.filter(f =>
      !schemaKeys.has(f.key) && !nodeTypeSpec.default_outputs.some(d => d.key === f.key)
    );

    if (hasSchemaFields) {
      // Schema fields completely replace defaults, then add custom fields
      return [...schemaOutputFields, ...customFields];
    } else {
      // No schema, use defaults + custom fields
      return [...nodeTypeSpec.default_outputs, ...customFields];
    }
  }, [schemaOutputFields, nodeTypeSpec.default_outputs, outputFields]);

  // Check if a field is from schema (non-removable)
  const isSchemaField = (fieldKey: string) =>
    schemaOutputFields.some(f => f.key === fieldKey);

  // Check if a field is a default field (non-removable)
  // Note: when schema fields exist, defaults are completely replaced
  const isDefaultField = (fieldKey: string) =>
    schemaOutputFields.length === 0 && nodeTypeSpec.default_outputs.some(f => f.key === fieldKey);

  return (
    <div className={`border-2 ${borderColorClass} rounded-lg ${bgColorClass} p-3 space-y-3 overflow-visible`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-200">
            <ArrowRight className="w-3 h-3 text-green-600" />
          </div>
          <span className="text-sm font-medium text-green-700">{title}</span>
          <span className="text-xs text-green-400">({allOutputFields.length})</span>
        </div>
        {canAddCustomOutputs && (
          <div className="relative" ref={popoverRef}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowTypePopover(!showTypePopover)}
              disabled={isDisabled}
              className="h-7 px-2 text-green-600 hover:bg-green-100"
            >
              <Plus className="w-3 h-3" />
            </Button>
            {/* Type Selection Popover */}
            {showTypePopover && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[120px]">
                {FIELD_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleAddFieldWithType(opt.value)}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getFieldTypeColor(opt.value) }}
                    />
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Output Fields List */}
      <div className="space-y-2">
        {allOutputFields.length === 0 ? (
          <p className="text-xs text-gray-400 italic text-center py-2">
            暂无输出字段，点击右上角 + 添加
          </p>
        ) : (
          allOutputFields.map((field) => {
            const fromSchema = isSchemaField(field.key);
            const fromDefault = isDefaultField(field.key);
            const canRemove = !fromSchema && !fromDefault;
            const canEdit = !fromSchema && !fromDefault;

            return (
              <OutputFieldRow
                key={field.key}
                field={field}
                customInputFields={customInputFields}
                onUpdateFieldName={canEdit ? (newKey) => updateOutputField(field.key, newKey) : () => {}}
                onNewFieldNameSet={(newKey) => handleNewFieldNameSet(field.key, newKey)}
                onSourceChange={(sourceKey) => updateOutputFieldSource(field.key, sourceKey)}
                onRemoveField={canRemove ? () => removeOutputField(field.key) : undefined}
                isDisabled={isDisabled}
                isEditingNew={editingNewFieldKey === field.key}
                isFromSchema={fromSchema}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

interface OutputFieldRowProps {
  field: OutputFieldDefinition;
  customInputFields: CustomInputField[];
  onUpdateFieldName: (newKey: string) => void;
  onNewFieldNameSet?: (newKey: string) => void;
  onSourceChange: (sourceKey: string | undefined) => void;
  onRemoveField?: () => void;
  isDisabled?: boolean;
  isEditingNew?: boolean;
  /** Whether this field is derived from an output schema */
  isFromSchema?: boolean;
}

const OutputFieldRow: React.FC<OutputFieldRowProps> = ({
  field,
  customInputFields,
  onUpdateFieldName,
  onNewFieldNameSet,
  onSourceChange,
  onRemoveField,
  isDisabled,
  isEditingNew = false,
  isFromSchema: _isFromSchema = false, // Reserved for future visual enhancements
}) => {
  const [isEditingName, setIsEditingName] = useState(isEditingNew);
  const [localFieldName, setLocalFieldName] = useState(isEditingNew ? '' : field.key);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Auto focus and select when editing field name
  useEffect(() => {
    if ((isEditingNew || isEditingName) && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingNew, isEditingName]);

  // Sync field name when field changes
  useEffect(() => {
    setLocalFieldName(field.key);
  }, [field.key]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSourceDropdown(false);
      }
    };
    if (showSourceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSourceDropdown]);

  const handleFieldNameClick = () => {
    if (!isDisabled && !isEditingNew && onRemoveField) {
      // Only allow editing custom fields (those that can be removed)
      setIsEditingName(true);
      setLocalFieldName(field.key);
    }
  };

  const handleFieldNameBlur = () => {
    const trimmed = localFieldName.trim();
    if (isEditingNew && onNewFieldNameSet) {
      onNewFieldNameSet(trimmed);
    } else {
      setIsEditingName(false);
      if (trimmed && trimmed !== field.key) {
        onUpdateFieldName(trimmed);
      }
    }
  };

  const handleFieldNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFieldNameBlur();
    } else if (e.key === 'Escape') {
      if (isEditingNew && onNewFieldNameSet) {
        onNewFieldNameSet('');
      } else {
        setIsEditingName(false);
        setLocalFieldName(field.key);
      }
    }
  };

  const handleSourceSelect = (sourceKey: string | undefined) => {
    onSourceChange(sourceKey);
    setShowSourceDropdown(false);
  };

  const isCustomField = !!onRemoveField;
  const hasInputFields = customInputFields.length > 0;
  const selectedSource = customInputFields.find((f) => f.key === field.value_source_input_key);

  return (
    <div className="flex flex-col gap-1.5 p-2 bg-white rounded border text-sm">
      {/* Row 1: Field name and type */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: getFieldTypeColor(field.type) }}
            />
            {isEditingName || isEditingNew ? (
              <input
                ref={nameInputRef}
                type="text"
                value={localFieldName}
                onChange={(e) => setLocalFieldName(e.target.value)}
                onBlur={handleFieldNameBlur}
                onKeyDown={handleFieldNameKeyDown}
                placeholder="变量名"
                className="flex-1 min-w-0 h-6 px-1 text-sm font-medium border border-green-400 rounded bg-green-50 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-gray-400 placeholder:font-normal"
              />
            ) : (
              <span
                onClick={handleFieldNameClick}
                className={`font-medium text-gray-700 ${isCustomField ? 'cursor-pointer hover:text-green-600 hover:underline' : ''}`}
                title={isCustomField ? `点击编辑: ${field.key}` : field.key}
              >
                {field.label || field.key}
                {/* Show key if different from label for clarity */}
                {field.label && field.label !== field.key && (
                  <span className="text-xs text-gray-400 font-normal ml-1">({field.key})</span>
                )}
              </span>
            )}
            {field.required && <span className="text-red-500 text-xs">*</span>}
            {field.output_schema_key && (
              <span className="text-blue-500 text-xs bg-blue-100 px-1 rounded">[schema]</span>
            )}
          </div>
          {/* Field type shown below */}
          <span className="text-[10px] text-gray-400 ml-4">{getFieldTypeLabel(field.type)}</span>
        </div>
        {onRemoveField && (
          <button
            onClick={onRemoveField}
            disabled={isDisabled}
            className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
            title="删除字段"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Row 2: Value source selector (only for custom fields with available inputs) */}
      {isCustomField && hasInputFields && !isEditingNew && (
        <div className="relative ml-4" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowSourceDropdown(!showSourceDropdown)}
            disabled={isDisabled}
            className={`w-full flex items-center justify-between px-2 py-1 text-xs border rounded transition-colors ${
              selectedSource
                ? 'border-green-300 bg-green-50 text-green-700'
                : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100'
            }`}
          >
            <span className="flex items-center gap-1.5 truncate">
              {selectedSource ? (
                <>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getFieldTypeColor(selectedSource.type) }} />
                  <span>← {selectedSource.label || selectedSource.key}</span>
                </>
              ) : (
                <span className="text-gray-400">选择值来源...</span>
              )}
            </span>
            <ChevronDown className="w-3 h-3 flex-shrink-0" />
          </button>

          {/* Source dropdown - positioned above to avoid clipping */}
          {showSourceDropdown && (
            <div className="absolute z-50 w-full bottom-full mb-1 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-auto">
              {/* Clear option */}
              <button
                type="button"
                onClick={() => handleSourceSelect(undefined)}
                className="w-full px-2 py-1.5 text-xs text-left text-gray-400 hover:bg-gray-50"
              >
                不映射
              </button>
              {/* Input fields */}
              {customInputFields.map((input) => {
                const isSelected = field.value_source_input_key === input.key;
                return (
                  <button
                    key={input.key}
                    type="button"
                    onClick={() => handleSourceSelect(input.key)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left ${
                      isSelected ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getFieldTypeColor(input.type) }} />
                    <span className="truncate">{input.label || input.key}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OutputFieldsSection;
